import * as React from 'react';
import {
  MdAdd,
  MdCheckCircle,
  MdDescription,
  MdPauseCircle,
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import DataGrid from '../../../Components/Table/DataGrid';
import DeleteModel from '../../../Components/Common/DeleteModel';
import { getAllAegisAiDocuments, deleteAegisAiDocument } from '../../../Services/AegisAiDocumentsService';
import { useAuth } from '../../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes, isSuperAdminUser } from '../../../Utils/Permissions';
import { APPRoutes } from '../../../Utils/Route';
import { toast } from 'react-toastify';
import PageTitle from '../../../Components/Common/PageTitle';
import SearchableDropdown from '../../../Components/Subscription/SearchableDropdown';
import { FaYoutube } from 'react-icons/fa';
import { LuFileText, LuGlobe as LuGlobe2 } from 'react-icons/lu';
import { AI_SOURCE_TYPES, AiRowActions, AiSearchBox, AiStatusPill, getFileMeta, inferAiSourceType, getAiSourceType } from '../AegisAiUi';

const STATUS_OPTIONS = ['All', 'Pending', 'Active', 'Inactive'];
const STATUS_DROPDOWN_OPTIONS = STATUS_OPTIONS.map((option) => ({ value: option, label: option }));
const SOURCE_DROPDOWN_OPTIONS = [
  { value: 'all', label: 'All Sources' },
  ...AI_SOURCE_TYPES.map((type) => ({ value: type.key, label: type.label })),
];

const getRowSourceKey = (row) => inferAiSourceType({
  sourceType: row.SourceType,
  url: row.SourceUrl,
  fileName: row.FileName,
});
const STATUS_BADGE_STYLE = {
  active: { backgroundColor: 'var(--hc-status-success-bg)', color: 'var(--hc-status-success-text)', label: 'Active', tone: 'success' },
  pending: { backgroundColor: 'var(--hc-status-pending-bg)', color: 'var(--hc-status-pending-text)', label: 'Pending', tone: 'pending' },
  inactive: { backgroundColor: 'var(--hc-status-inactive-bg)', color: 'var(--hc-status-inactive-text)', label: 'Inactive', tone: 'inactive' },
};

const getStatusBadgeMeta = (statusValue) => {
  const normalized = (statusValue || '').toLowerCase().trim();
  if (STATUS_BADGE_STYLE[normalized]) {
    return STATUS_BADGE_STYLE[normalized];
  }
  return {
    backgroundColor: 'var(--hc-status-pending-bg)',
    color: 'var(--hc-status-pending-text)',
    label: statusValue || 'Pending',
    tone: 'pending',
  };
};

export default function AegisAiDocumentsList() {
  const navigate = useNavigate();
  const { token, permissions, decodedToken } = useAuth();

  const userPermissionCodes = React.useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken],
  );

  const isSuperAdmin = isSuperAdminUser(decodedToken);

  const canViewDocument = permissionHas(userPermissionCodes, PermissionCodes.DOCUMENTS_VIEW, decodedToken);
  const canCreateDocument = permissionHas(userPermissionCodes, PermissionCodes.DOCUMENTS_CREATE, decodedToken);
  const canEditDocument = permissionHas(userPermissionCodes, PermissionCodes.DOCUMENTS_EDIT, decodedToken);
  const canDeleteDocument = permissionHas(userPermissionCodes, PermissionCodes.DOCUMENTS_DELETE, decodedToken);

  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState('All');
  const [sourceType, setSourceType] = React.useState('all');
  const [rows, setRows] = React.useState([]);
  const [summary, setSummary] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState('');

  const loadDocuments = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllAegisAiDocuments(token);
      if (response?.is_success) {
        const documents = Array.isArray(response?.data?.Documents) ? response.data.Documents : [];
        setRows(documents);
        setSummary(response?.data || null);
      } else if (response?.is_success === false && response?.status_code === 500) {
        toast.error('Internal server error. Please Contact Support Team.');
        setRows([]);
        setSummary(null);
      } else {
        toast.error(response?.message || 'Failed to load documents');
        setRows([]);
        setSummary(null);
      }
    } catch (error) {
      console.error(error);
      setRows([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => {
    if (token) {
      loadDocuments();
    }
  }, [token, loadDocuments]);

  const handleCreate = React.useCallback(() => {
    navigate(APPRoutes.AEGIS_AI_DOCUMENTS_FORM);
  }, [navigate]);

  const handleView = React.useCallback((row) => {
    navigate(APPRoutes.AEGIS_AI_DOCUMENTS_VIEW.replace(':id', row.Id), { state: { document: row } });
  }, [navigate]);

  const handleEdit = React.useCallback((row) => {
    navigate(APPRoutes.AEGIS_AI_DOCUMENTS_EDIT.replace(':id', row.Id));
  }, [navigate]);

  const handleDeleteClick = React.useCallback((row) => {
    setDeleteError('');
    setDeleteTarget(row);
  }, []);

  const handleDeleteClose = React.useCallback(() => {
    if (deleteLoading) return;
    setDeleteTarget(null);
    setDeleteError('');
  }, [deleteLoading]);

  const handleDeleteConfirm = React.useCallback(async () => {
    if (!deleteTarget?.Id) return;
    try {
      setDeleteLoading(true);
      setDeleteError('');
      const response = await deleteAegisAiDocument(deleteTarget.Id, token);
      if (response?.is_success) {
        setRows((prev) => prev.filter((doc) => doc.Id !== deleteTarget.Id));
        setDeleteTarget(null);
        loadDocuments();
        toast.success(response?.message || 'Document deleted successfully.');
      } else if (response?.is_success === false && response?.status_code === 500) {
        setDeleteError('Internal Server Error. Please Contact Support Team.');
      } else {
        setDeleteError(response?.message || 'Failed to delete document.');
      }
    } catch (error) {
      console.error(error);
      setDeleteError('Something went wrong while deleting the document.');
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteTarget, token, loadDocuments]);

  const filteredRows = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    const filterStatus = status.toLowerCase();
    return rows.filter((row) => {
      const rowStatus = (row.Status || row.IndexingStatus || '').toLowerCase();
      const matchesStatus = filterStatus === 'all' || rowStatus === filterStatus;
      const matchesSource = sourceType === 'all' || getRowSourceKey(row) === sourceType;
      const matchesSearch =
        !term ||
        row.Title?.toLowerCase().includes(term) ||
        row.ClientName?.toLowerCase().includes(term);
      return matchesStatus && matchesSource && matchesSearch;
    });
  }, [rows, search, status, sourceType]);

  const stats = React.useMemo(() => {
    const total = summary?.TotalDocuments ?? rows.length;
    const active = rows.filter((r) => (r.Status || '').toLowerCase() === 'active').length || (summary?.ActiveDocuments ?? 0);
    const pending = rows.filter((r) => {
      const s = (r.Status || r.IndexingStatus || '').toLowerCase();
      return s === 'pending' || s === 'not_indexed';
    }).length;
    const inactive = rows.filter((r) => (r.Status || '').toLowerCase() === 'inactive').length || (summary?.InactiveDocuments ?? 0);

    return [
      {
        label: 'Total Documents',
        value: total,
        icon: <MdDescription size={22} color="var(--hc-icon-blue-text)" />,
        iconBg: 'var(--hc-icon-blue-bg)',
        accentClass: 'border-primary',
        valueClass: 'text-primary',
        colClass: 'col-12 col-sm-6 col-lg-3',
      },
      {
        label: 'Active',
        value: active,
        icon: <MdCheckCircle size={22} color="var(--hc-status-success-text)" />,
        iconBg: 'var(--hc-icon-green-bg)',
        accentClass: 'border-success',
        valueClass: 'text-success',
        colClass: 'col-12 col-sm-6 col-lg-3',
      },
      {
        label: 'Pending',
        value: pending,
        icon: <MdPauseCircle size={22} color="var(--hc-status-pending-text)" />,
        iconBg: 'var(--hc-status-pending-bg)',
        accentClass: 'border-warning',
        valueClass: 'text-warning',
        colClass: 'col-12 col-sm-6 col-lg-3',
      },
      {
        label: 'Inactive',
        value: inactive,
        icon: <MdPauseCircle size={22} color="var(--hc-status-inactive-text)" />,
        iconBg: 'var(--hc-status-inactive-bg)',
        accentClass: 'border-secondary',
        valueClass: 'text-secondary',
        colClass: 'col-12 col-sm-6 col-lg-3',
      },
    ];
  }, [rows, summary]);

  const columns = React.useMemo(() => {
    const cols = [
      {
        id: 'Title',
        label: 'Document',
        minWidth: 60,
        format: (value, row) => {
          const fileMeta = getFileMeta(row.FileName);
          const sourceType = getAiSourceType(getRowSourceKey(row));

          let CellIcon = sourceType.Icon;
          let cellColor = sourceType.color;
          let cellExt = null;

          if (sourceType.key === 'document') {
            CellIcon = fileMeta.Icon;
            cellColor = fileMeta.color;
          } else if (sourceType.key === 'audio' || sourceType.key === 'video') {
            CellIcon = fileMeta.Icon || sourceType.Icon;
            cellColor = sourceType.color;
          } else if (sourceType.key === 'youtube') {
            CellIcon = FaYoutube;
            cellColor = '#e52d27';
          } else if (sourceType.key === 'website') {
            CellIcon = LuGlobe2;
            cellColor = '#3c9fa2';
          } else if (sourceType.key === 'custom_text') {
            CellIcon = LuFileText;
            cellColor = '#0d9488';
          }

          return (
            <div className="hc-ai-cell d-flex align-items-center">
              <span className="hc-ai-cell-icon d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ color: cellColor }}>
                <CellIcon size={20} />
              </span>
              <div className="d-flex flex-column" style={{ minWidth: 0 }}>
                <div className="d-flex align-items-center gap-2">
                  <span className="hc-ai-cell-title fw-bold">{value}</span>
                  {row.IsOcr ? (
                    <span
                      className="badge rounded-pill fw-semibold flex-shrink-0"
                      style={{
                        backgroundColor: '#e0f2fe',
                        color: '#0284c7',
                        border: '1px solid #bae6fd',
                        fontSize: '10px',
                        padding: '1px 6px',
                      }}
                      title="Extracted with Vision AI OCR enabled"
                    >
                      OCR
                    </span>
                  ) : null}
                </div>
                {row.Description ? <span className="hc-ai-cell-sub overflow-hidden" title={row.Description}>{row.Description}</span> : null}
              </div>
            </div>
          );
        },
      },
    ];

    if (isSuperAdmin) {
      cols.push({
        id: 'ClientName',
        label: 'Client',
        minWidth: 100,
        format: (value) => value || <span className="text-secondary">—</span>,
      });
    }

    cols.push(
      {
        id: 'Status',
        label: 'Status',
        minWidth: 60,
        align: 'center',
        format: (value, row) => {
          const rawStatus = value || row?.IndexingStatus || 'Pending';
          const meta = getStatusBadgeMeta(rawStatus);
          return <AiStatusPill tone={meta.tone} label={meta.label} />;
        },
      },
      {
        id: 'CreatedBy',
        label: 'Created By',
        minWidth: 70,
      },
    );

    if (canViewDocument || canEditDocument || canDeleteDocument) {
      cols.push({
        id: 'actions',
        label: 'Actions',
        minWidth: 80,
        align: 'center',
        format: (_value, row) => (
          <AiRowActions
            onView={canViewDocument ? () => handleView(row) : undefined}
            onEdit={canEditDocument ? () => handleEdit(row) : undefined}
            onDelete={canDeleteDocument ? () => handleDeleteClick(row) : undefined}
          />
        ),
      });
    }

    return cols;
  }, [isSuperAdmin, handleView, handleEdit, handleDeleteClick, canViewDocument, canEditDocument, canDeleteDocument]);

  return (
    <>
      <PageTitle title="Aegis AI Documents - Aegis 360" />
      <div className="hc-ai-page">
        <div className="d-flex flex-md-row flex-column align-items-start align-items-md-center justify-content-between gap-2 mb-4">
          <div>
            <h4 className="fw-bold mb-1 hc-text-primary fs-3">Aegis AI Documents</h4>
            <p className="text-secondary mb-0 small">
              Manage the source documents each client's RAG assistant is allowed to reference
            </p>
          </div>
          {canCreateDocument ? (
            <button onClick={handleCreate} type="button" className="hc-ai-btn overflow-hidden d-inline-flex align-items-center justify-content-center gap-2 fw-bold text-nowrap hc-ai-btn-primary">
              <MdAdd size={20} />
              <span>Upload Document</span>
            </button>
          ) : null}
        </div>

        {loading ? (
          <div className="row g-3 mb-3">
            {[1, 2, 3, 4].map((_, i) => (
              <div key={i} className="col-12 col-sm-6 col-lg-3">
                <div className="bg-white p-4 rounded-4 shadow">
                  <div className="skeleton mb-2" style={{ height: 16, width: '60%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 28, width: '40%' }} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="row g-3 mb-3">
            {stats.map((stat) => (
              <div key={stat.label} className={stat.colClass || 'col-12 col-sm-6 col-lg-3'}>
                <div className="position-relative overflow-hidden bg-white p-4 rounded-4 shadow p-3 d-flex align-items-center gap-3 h-100 hc-list-kpi-card">
                  <div className={`position-absolute start-0 top-0 h-100 border-start border-5 ${stat.accentClass}`} />
                  <div className="d-inline-flex align-items-center justify-content-center rounded-3" style={{ width: 44, height: 44, backgroundColor: stat.iconBg }}>
                    {stat.icon}
                  </div>
                  <div className="d-flex flex-column fw-bold lh-sm">
                    <span className="text-dark" style={{ fontSize: '0.85rem' }}>{stat.label}</span>
                    <span className={`fw-bold fs-5 ${stat.valueClass}`}>{stat.value}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="hc-ai-panel bg-white">
          <div className="hc-ai-panel-head d-flex flex-wrap align-items-center justify-content-between mb-3">
            <h2 className="hc-ai-panel-title d-flex align-items-center m-0">
              <span className="hc-ai-card-head-icon d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 34, height: 34, borderRadius: 10 }}>
                <MdDescription size={18} />
              </span>
              Documents
            </h2>
          </div>

          <div className="row g-2 mb-3">
            <div className="col-12 col-md-6 col-lg-6">
              <AiSearchBox value={search} onChange={setSearch} placeholder="Search Document By Title" />
            </div>
            <div className="col-12 col-sm-6 col-md-3 hc-ai-filter">
              <SearchableDropdown placeholder="Select source type" options={SOURCE_DROPDOWN_OPTIONS} value={sourceType} onChange={setSourceType} showSearch={true} />
            </div>
            <div className="col-12 col-sm-6 col-md-3 hc-ai-filter">
              <SearchableDropdown placeholder="Select status" options={STATUS_DROPDOWN_OPTIONS} value={status} onChange={setStatus} showSearch={true} />
            </div>
          </div>

          <div className="hc-ai-grid-wrap bg-white">
            <DataGrid
              columns={columns}
              rows={filteredRows}
              loading={loading}
              skeletonRows={6}
              defaultRowsPerPage={10}
              stickyHeader
              getRowId={(row) => row.Id}
            />
          </div>
        </div>
      </div>

      <DeleteModel
        isOpen={Boolean(deleteTarget)}
        title="Delete Document"
        entityLabel="document"
        itemName={deleteTarget?.Title || ''}
        description="You are about to remove this document. It will no longer be available to the client's RAG assistant."
        confirmText="Delete Document"
        isLoading={deleteLoading}
        errorMessage={deleteError}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
}
